import type { ReactNode } from "react";
import { Clock } from "lucide-react";

import { IntegrationBrandIcon } from "@/components/site/primitives/integration-brand-icon";
import { MarketingTrackedLink } from "@/components/site/primitives/marketing-tracked-link";
import {
  buildInstantScanStartHref,
  instantScanCtaId,
  instantScanProviders,
  type InstantScanPlacement,
  type InstantScanProvider,
} from "@/lib/instant-scan-entry";
import { cn } from "@/lib/utils";

type ScanEventContext = {
  placement: InstantScanPlacement;
  /** Distinguishes responsive duplicates, e.g. the mobile pill. */
  routeSuffix?: string;
};

function scanEventProperties(
  provider: InstantScanProvider,
  { placement, routeSuffix }: ScanEventContext,
) {
  return {
    app_area: "instant_scan",
    cta_id: instantScanCtaId(placement),
    placement,
    provider: provider.slug,
    route_name: `home.instant_scan.${placement}${routeSuffix ? `.${routeSuffix}` : ""}`,
  };
}

type ScanToolLinkProps = ScanEventContext & {
  provider: InstantScanProvider;
  className?: string;
  "aria-label"?: string;
  children: ReactNode;
};

function ScanToolLink({
  provider,
  className,
  children,
  "aria-label": ariaLabel,
  ...context
}: ScanToolLinkProps) {
  return (
    <MarketingTrackedLink
      href={buildInstantScanStartHref(provider.slug, context.placement)}
      prefetch={false}
      aria-label={ariaLabel}
      className={className}
      eventName="instant_scan_cta_clicked"
      eventProperties={scanEventProperties(provider, context)}
    >
      {children}
    </MarketingTrackedLink>
  );
}

// The shared Button's focus treatment.
const focusRing =
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** "Free | 2-minute scan | Read-only", in the hero badge's vocabulary. */
export function InstantScanTag({
  surface = "muted",
  className,
}: {
  surface?: "muted" | "white";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-8 items-center gap-2.5 whitespace-nowrap rounded-full border border-zinc-200/80 pl-2 pr-3.5 text-[13px] font-medium tracking-[-0.015em] text-zinc-700 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.18),0_2px_6px_rgba(15,23,42,0.05)] sm:h-[34px] sm:gap-3 sm:text-sm",
        surface === "white" ? "bg-white" : "bg-[#F4F4F5]",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-[22px] items-center justify-center rounded-full border border-zinc-200 text-foreground",
          surface === "white" ? "bg-[#F4F4F5]" : "bg-white",
        )}
      >
        <Clock className="size-3" strokeWidth={2.2} />
      </span>
      Free
      <span aria-hidden className="block h-3.5 w-px bg-zinc-300" />
      <span className="sr-only">, </span>
      2-minute scan
      <span aria-hidden className="block h-3.5 w-px bg-zinc-300" />
      <span className="sr-only">, </span>
      Read-only
    </span>
  );
}

/** The tool logos are the buttons: one click opens that tool's scan, no menu in between. */
export function InstantScanToolButtons({
  className,
  ...context
}: ScanEventContext & { className?: string }) {
  return (
    <div
      role="group"
      aria-label="Start a scan with"
      className={cn(
        "grid w-full grid-cols-2 gap-2.5 sm:flex sm:w-auto sm:flex-wrap",
        className,
      )}
    >
      {instantScanProviders.map((provider) => (
        <ScanToolLink
          key={provider.slug}
          provider={provider}
          {...context}
          className={cn(
            "site-pressable inline-flex h-[52px] items-center justify-center gap-2.5 whitespace-nowrap rounded-[14px] border border-zinc-200 bg-white px-3 text-base font-medium tracking-[-0.01em] text-foreground shadow-[0_1px_2px_rgba(9,9,11,0.05),0_6px_16px_-10px_rgba(9,9,11,0.18)] hover:border-zinc-400 active:scale-[0.97] sm:pl-4 sm:pr-5",
            focusRing,
          )}
        >
          <IntegrationBrandIcon slug={provider.slug} />
          {provider.name}
        </ScanToolLink>
      ))}
    </div>
  );
}

/** Logo-only circles for the floating pill and the first step. */
export function InstantScanToolCircles({
  size = "md",
  className,
  ...context
}: ScanEventContext & { size?: "sm" | "md"; className?: string }) {
  return (
    <span className={cn("flex", size === "md" ? "gap-1.5" : "gap-1", className)}>
      {instantScanProviders.map((provider) => (
        <ScanToolLink
          key={provider.slug}
          provider={provider}
          {...context}
          aria-label={`Scan ${provider.name}`}
          className={cn(
            "site-pressable flex shrink-0 items-center justify-center rounded-full border border-zinc-200/90 bg-white shadow-[0_1px_2px_rgba(9,9,11,0.05)] hover:border-zinc-400 hover:bg-zinc-50 active:scale-[0.95]",
            size === "md" ? "size-12" : "size-11",
            focusRing,
          )}
        >
          <IntegrationBrandIcon slug={provider.slug} />
        </ScanToolLink>
      ))}
    </span>
  );
}
